import { formatCategory, formatStatLine } from '../../components/GameTerms';
import { renderItemIcon, getItemIconUrl } from '../../components/ItemIcon';
import { getConfig, getDiscoveredUniques } from './types';
import { StateManager } from '../../../managers';
import type { ItemBaseDef, ItemData, UniqueItemData } from '../../../types';

export interface UniquesState {
  expandedBaseId: string | null;
}

export function createUniquesState(): UniquesState {
  return { expandedBaseId: null };
}

const CATEGORY_ORDER = ['weapon', 'armor', 'offhand', 'accessory'];

const FAMILY_ORDER: string[] = [
  'sword', 'hammer', 'staff', 'dagger', 'wand', 'fist', 'bow', 'fan',
  'plate', 'padded', 'leather', 'lamellar', 'shadeweave', 'robe',
  'ring', 'amulet', 'shield',
];

/** Merge a unique with its base to get the effective display def (category/family/implicit). */
function resolveUnique(u: UniqueItemData, itemBases: Record<string, ItemBaseDef>) {
  const base = itemBases[u.baseId];
  return {
    unique: u,
    base,
    category: base?.category ?? 'weapon',
    family: base?.family ?? '',
    implicitAffixId: u.implicitAffixId ?? base?.implicitAffixId,
    masteryRequirements: u.masteryRequirements ?? base?.masteryRequirements ?? [],
  };
}

/** Uniques chapter — shows all uniques, silhouettes for undiscovered. */
export function renderUniques(content: HTMLElement, state: UniquesState): void {
  const config = getConfig();
  if (!config) { content.innerHTML = '<p>Loading...</p>'; return; }

  const allUniques = Object.values(config.uniqueBases ?? {});
  const discovered = getDiscoveredUniques();
  const discoveredCount = allUniques.filter(u => discovered.has(u.id)).length;

  // Owned counts for badge display
  const items = (StateManager.get('itemData') as ItemData[] | null) ?? [];
  const ownedCounts = new Map<string, number>();
  for (const i of items.filter(it => it.rarity === 'unique')) {
    ownedCounts.set(i.baseType, (ownedCounts.get(i.baseType) ?? 0) + 1);
  }

  if (state.expandedBaseId) {
    const unique = allUniques.find(u => u.id === state.expandedBaseId);
    if (unique && discovered.has(unique.id)) {
      renderUniqueDetail(content, state, unique);
      return;
    }
    state.expandedBaseId = null;
  }

  const renderCard = (u: UniqueItemData): string => {
    const resolved = resolveUnique(u, config.itemBases);
    const isDiscovered = discovered.has(u.id);
    if (!isDiscovered) {
      return `
        <div class="codex-item-card codex-unique-card undiscovered" data-unique-id="${u.id}">
          <div class="codex-item-icon"><img class="item-icon item-icon--lg codex-sprite-silhouette" src="${getItemIconUrl(u.id, resolved.category)}" alt="" width="56" height="56" draggable="false"></div>
          <div class="codex-item-info">
            <div class="codex-item-name codex-unique-name">???</div>
            <div class="codex-item-meta">${formatCategory(resolved.category)}</div>
          </div>
        </div>
      `;
    }
    const count = ownedCounts.get(u.id) ?? 0;
    const countBadge = count > 1 ? `<span class="codex-unique-count">×${count}</span>` : '';
    return `
      <div class="codex-item-card codex-unique-card" data-unique-id="${u.id}">
        <div class="codex-item-icon">${renderItemIcon(u.id, resolved.category, 'lg')}</div>
        <div class="codex-item-info">
          <div class="codex-item-name codex-unique-name">${u.uniqueName}${countBadge}</div>
          <div class="codex-item-meta">${formatCategory(resolved.category)} · ${resolved.family.charAt(0).toUpperCase() + resolved.family.slice(1)}</div>
          ${u.flavorText ? `<div class="codex-unique-flavor">${u.flavorText}</div>` : ''}
        </div>
      </div>
    `;
  };

  // Group by category. Within a category, sort by family then ilvl.
  // Undiscovered uniques sink to the bottom of their group.
  const groups = new Map<string, UniqueItemData[]>();
  for (const u of allUniques) {
    const cat = resolveUnique(u, config.itemBases).category;
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat)!.push(u);
  }
  const sortedGroups = [...groups.entries()].sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a[0]);
    const bi = CATEGORY_ORDER.indexOf(b[0]);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  for (const [, list] of sortedGroups) {
    list.sort((a, b) => {
      const ad = discovered.has(a.id) ? 0 : 1;
      const bd = discovered.has(b.id) ? 0 : 1;
      if (ad !== bd) return ad - bd;
      const af = resolveUnique(a, config.itemBases).family;
      const bf = resolveUnique(b, config.itemBases).family;
      const afi = FAMILY_ORDER.indexOf(af);
      const bfi = FAMILY_ORDER.indexOf(bf);
      const familyDelta = (afi === -1 ? 99 : afi) - (bfi === -1 ? 99 : bfi);
      if (familyDelta !== 0) return familyDelta;
      return (a.ilvl || 0) - (b.ilvl || 0);
    });
  }

  const groupHtml = sortedGroups.map(([cat, list]) => {
    const label = formatCategory(cat);
    return `
      <div class="codex-family-group">
        <div class="codex-family-header">${label} <span class="codex-family-count">${list.length}</span></div>
        <div class="codex-unique-grid">${list.map(renderCard).join('')}</div>
      </div>
    `;
  }).join('');

  content.innerHTML = `
    <h2 class="codex-section-title">Uniques</h2>
    <p class="codex-section-subtitle">${discoveredCount} / ${allUniques.length} discovered</p>
    ${groupHtml || '<p class="codex-empty">No uniques to show</p>'}
  `;

  // Only discovered uniques are clickable
  content.querySelectorAll('[data-unique-id]:not(.undiscovered)').forEach(card => {
    card.addEventListener('click', () => {
      state.expandedBaseId = (card as HTMLElement).dataset.uniqueId!;
      renderUniques(content, state);
      content.scrollTop = 0;
    });
  });
}

function renderUniqueDetail(content: HTMLElement, state: UniquesState, unique: UniqueItemData): void {
  const config = getConfig();
  if (!config) return;

  const resolved = resolveUnique(unique, config.itemBases);
  const heroSrc = getItemIconUrl(unique.id, resolved.category, true);
  const implicitAffix = resolved.implicitAffixId ? config.affixes?.[resolved.implicitAffixId] : null;

  // Implicit line — read the best eligible tier at the unique's ilvl
  let implicitLine = '';
  if (implicitAffix) {
    const eligible = implicitAffix.tiers.filter(t => t.requiredLevel <= (unique.ilvl || 1));
    const best = eligible[eligible.length - 1];
    if (best) {
      const rep = Math.floor((best.minValue + best.maxValue) / 2);
      implicitLine = formatStatLine(implicitAffix.stat, implicitAffix.modifier, rep);
    } else {
      implicitLine = implicitAffix.name;
    }
  }

  // Fixed explicits — each one is a {affixId, roll} with a known value
  const explicitLines = unique.fixedExplicits.map(fx => {
    const def = config.affixes?.[fx.affixId];
    if (!def) return '';
    return formatStatLine(def.stat, def.modifier, fx.roll);
  }).filter(Boolean);

  const reqs = resolved.masteryRequirements.map(r => {
    const name = config.masteries?.[r.masteryId]?.name ?? r.masteryId;
    return `${name} ${r.rank}`;
  });

  content.innerHTML = `
    <button class="codex-back-btn" id="codex-unique-back">Back</button>
    <div class="codex-detail-page">
      <div class="unique-hero">
        <div class="unique-hero-art">
          <img src="${heroSrc}" alt="" draggable="false" onerror="this.style.display='none'">
        </div>
        <div class="unique-hero-name">${unique.uniqueName}</div>
        <div class="unique-hero-meta">${formatCategory(resolved.category)} · ${resolved.family}</div>
        ${reqs.length > 0 ? `<div class="unique-hero-reqs">${reqs.join(' · ')}</div>` : ''}
      </div>

      <div class="codex-detail-card unique-affixes">
        ${implicitLine ? `
          <div class="unique-affix-section">
            <div class="unique-affix-label">Innate</div>
            <div class="unique-affix-line implicit">${implicitLine}</div>
          </div>
        ` : ''}
        ${implicitLine && explicitLines.length > 0 ? '<div class="unique-affix-divider"></div>' : ''}
        ${explicitLines.length > 0 ? `
          <div class="unique-affix-section">
            <div class="unique-affix-label">Unique Affixes</div>
            ${explicitLines.map(l => `<div class="unique-affix-line">${l}</div>`).join('')}
          </div>
        ` : ''}
      </div>

      ${unique.flavorText ? `
        <div class="unique-flavor">${unique.flavorText}</div>
      ` : ''}
    </div>
  `;

  content.querySelector('#codex-unique-back')?.addEventListener('click', () => {
    state.expandedBaseId = null;
    renderUniques(content, state);
    content.scrollTop = 0;
  });
}
